import { useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';

const money = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;
const pct = (n: number) => `${Math.round(n * 100)}%`;

/**
 * "Your client list is your retirement."
 *
 * Every number here is computed from the operator's own records — that provenance is the
 * product. Nothing on this screen is typed in by the person selling.
 */
export default function Marketplace() {
  const mine = useQuery(api.marketplace.myRound, {});
  const gate = useQuery(api.marketplace.gateStatus, {});
  const listings = useQuery(api.marketplace.browse, gate?.enabled ? {} : 'skip');
  const intros = useQuery(api.marketplace.incomingIntros, {});

  const createListing = useMutation(api.marketplace.createListing);
  const publish = useMutation(api.marketplace.publish);
  const withdraw = useMutation(api.marketplace.withdraw);
  const requestIntro = useMutation(api.marketplace.requestIntro);
  const respond = useMutation(api.marketplace.respondToIntro);

  const [title, setTitle] = useState('');
  const [region, setRegion] = useState('WA');
  const [price, setPrice] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (mine === undefined) return <p className="muted">Loading…</p>;
  const m = mine.metrics;

  return (
    <>
      <h1>Your round</h1>
      <p className="muted">
        What your client list looks like as a sellable asset — measured from your own records,
        not from a form.
      </p>

      <div className="tiles">
        <div className="tile"><span className="n">{m.clientCount}</span><span className="l">clients</span></div>
        <div className="tile"><span className="n">{m.activeAssetCount}</span><span className="l">items on register</span></div>
        <div className="tile">
          <span className="n">{money(m.annualRecurringRevenue)}</span>
          <span className="l">billed last 12 months</span>
        </div>
        <div className="tile">
          <span className="n">{m.retentionBasis === 0 ? '—' : pct(m.retentionRate)}</span>
          <span className="l">
            {m.retentionBasis === 0
              ? 'retention: too early to say'
              : `retention (${m.retentionBasis} clients)`}
          </span>
        </div>
      </div>

      {m.retentionBasis === 0 ? (
        <div className="card">
          <p className="muted small">
            Retention only counts clients who have been on your books more than a year — a
            client you picked up last month cannot prove anything yet. It will appear here
            once your history is long enough to mean something.
          </p>
        </div>
      ) : null}

      {error ? (
        <div className="card"><p className="small" style={{ color: 'var(--alert)' }}>{error}</p></div>
      ) : null}

      {!gate?.enabled ? (
        <div className="card">
          <strong>Listings are not open yet.</strong>
          <p className="muted small">{gate?.note}</p>
          <p className="muted small">
            Nothing above is shared with anyone. It is your data, shown back to you.
          </p>
        </div>
      ) : (
        <>
          <h2>{mine.listing ? 'Your listing' : 'List your round'}</h2>
          <div className="card">
            {mine.listing ? (
              <>
                <div className="spread">
                  <div>
                    <strong>{mine.listing.title}</strong>
                    <p className="muted small">
                      {mine.listing.region} · {mine.listing.status}
                      {mine.listing.askingPrice ? ` · asking ${money(mine.listing.askingPrice)}` : ''}
                    </p>
                  </div>
                  <div className="row">
                    {mine.listing.status !== 'listed' ? (
                      <button onClick={() => void publish({ listingId: mine.listing!._id })}>
                        Publish
                      </button>
                    ) : (
                      <button className="quiet" onClick={() => void withdraw({ listingId: mine.listing!._id })}>
                        Withdraw
                      </button>
                    )}
                  </div>
                </div>
                <p className="muted small">
                  Publishing recomputes your numbers from live data, so a listing cannot go stale.
                  Buyers see the figures and your region — never your name or your clients.
                </p>
              </>
            ) : (
              <>
                <label>Listing title</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Perth metro round" />
                <label>Region</label>
                <input value={region} onChange={(e) => setRegion(e.target.value)} />
                <label>Asking price (optional)</label>
                <input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="45000" />
                <button
                  style={{ marginTop: '.75rem' }}
                  disabled={!title.trim()}
                  onClick={async () => {
                    setError(null);
                    try {
                      await createListing({
                        title: title.trim(),
                        region: region.trim(),
                        ...(price ? { askingPrice: Math.round(Number(price) * 100) } : {}),
                      });
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'Could not create the listing');
                    }
                  }}
                >
                  Create listing
                </button>
              </>
            )}
          </div>

          <h2>Rounds for sale</h2>
          <div className="card">
            <table>
              <thead>
                <tr><th>Round</th><th>Region</th><th>Clients</th><th>Revenue</th><th>Retention</th><th /></tr>
              </thead>
              <tbody>
                {(listings ?? []).map((l) => (
                  <tr key={l._id}>
                    <td>{l.title}</td>
                    <td className="muted">{l.region}</td>
                    <td>{l.metrics.clientCount}</td>
                    <td>{money(l.metrics.annualRecurringRevenue)}</td>
                    <td>{pct(l.metrics.retentionRate)}</td>
                    <td>
                      <button
                        className="quiet"
                        onClick={() => void requestIntro({
                          listingId: l._id as Id<'listings'>,
                          message: 'I would like an introduction about this round.',
                        })}
                      >
                        Request intro
                      </button>
                    </td>
                  </tr>
                ))}
                {listings?.length === 0 ? (
                  <tr><td colSpan={6} className="muted">No rounds listed right now.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      )}

      {intros && intros.length > 0 ? (
        <>
          <h2>Introduction requests</h2>
          {intros.map(({ request, buyer }) => (
            <div className="card" key={request._id}>
              <p className="small">
                An operator in <strong>{buyer.jurisdiction ?? 'another state'}</strong> wants an
                introduction.
              </p>
              <p className="muted small">&ldquo;{request.message}&rdquo;</p>
              <div className="row">
                <button onClick={() => void respond({ requestId: request._id, approve: true })}>
                  Approve &amp; share data room
                </button>
                <button className="quiet" onClick={() => void respond({ requestId: request._id, approve: false })}>
                  Decline
                </button>
              </div>
            </div>
          ))}
        </>
      ) : null}

      <div className="card">
        <p className="muted small">
          TagVault makes the introduction and nothing else — no escrow, no funds, no contracts.
          Anything you agree with a buyer is between the two of you and your advisors.
        </p>
      </div>
    </>
  );
}
